import apiCall from './api';
import * as XLSX from 'xlsx';

/**
 * ✅ EXPORT: Service responsable de la génération des exports Excel.
 */
const exportService = {

  /**
   * Récupère les données d'export "CA par produit" depuis le backend.
   */
  async getProductSalesData(startDate, endDate) {
    try {
      console.log(`📊 Récupération données export : ${startDate} → ${endDate}`);

      const response = await apiCall.get('/manager/export/products-sales', {
        startDate,
        endDate
      });

      if (response.success) {
        return { success: true, data: response.data };
      }

      return {
        success: false,
        message: response.message || 'Erreur lors de la récupération des données'
      };

    } catch (error) {
      console.error('❌ Erreur export :', error);
      return {
        success: false,
        message: error.message || 'Erreur de connexion'
      };
    }
  },

  /**
   * Génère un fichier Excel à partir des données et déclenche le téléchargement.
   * Format simplifié : 5 colonnes (Produit, Catégorie, Qté, CA total, Prix unitaire moyen).
   */
  generateProductSalesExcel(exportData) {
    try {
      if (!exportData || !exportData.products) {
        throw new Error('Aucune donnée à exporter');
      }

      console.log(`📝 Génération Excel : ${exportData.products.length} produits`);

      // ===== En-tête du fichier =====
      const headers = [
        ['RAPPORT DES VENTES PAR PRODUIT'],
        [],
        ['Boutique :', exportData.boutiqueName || 'N/A'],
        ['Période :', exportData.periodLabel || 'N/A'],
        ['Export généré le :', new Date().toLocaleString('fr-FR')],
        [],
        // Ligne d'en-têtes du tableau (5 colonnes)
        [
          'Produit',
          'Catégorie',
          'Qté vendue',
          'CA total (FCFA)',
          'Prix unitaire (FCFA)'
        ]
      ];

      // ===== Lignes de données (5 colonnes alignées avec les en-têtes) =====
      const dataRows = exportData.products.map(p => [
        p.productName || '',
        p.categoryName || 'Sans catégorie',
        p.quantitySold || 0,
        p.totalRevenue || 0,
        p.averagePrice || 0
      ]);

      // ===== Ligne des totaux =====
      const totalRow = [
        'TOTAL',
        '',
        exportData.totalQuantity || 0,
        exportData.totalRevenue || 0,
        '' // Pas de moyenne globale (chaque produit a son propre prix moyen)
      ];

      // Construction finale
      const sheetData = [
        ...headers,
        ...dataRows,
        [], // Ligne vide avant totaux
        totalRow
      ];

      // ===== Création de la feuille =====
      const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

      // Largeur des colonnes (5 colonnes)
      worksheet['!cols'] = [
        { wch: 35 }, // Produit
        { wch: 22 }, // Catégorie
        { wch: 12 }, // Qté vendue
        { wch: 18 }, // CA total
        { wch: 20 }  // Prix unitaire
      ];

      // Fusion du titre (ligne 1, colonnes A à E)
      worksheet['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 4 } }
      ];

      // Style basique sur le titre
      if (worksheet['A1']) {
        worksheet['A1'].s = {
          font: { bold: true, sz: 16 },
          alignment: { horizontal: 'center' }
        };
      }

      // ===== Création du workbook =====
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'CA par produit');

      // ===== Téléchargement =====
      const fileName = this._buildFileName(exportData);
      XLSX.writeFile(workbook, fileName);

      console.log(`✅ Excel généré : ${fileName}`);

      return {
        success: true,
        fileName,
        rowsExported: exportData.products.length
      };

    } catch (error) {
      console.error('❌ Erreur génération Excel :', error);
      return {
        success: false,
        message: error.message || 'Erreur lors de la génération du fichier'
      };
    }
  },

  /**
   * Pipeline complet : récupérer les données + générer l'Excel.
   */
  async exportProductSalesToExcel(startDate, endDate) {
    // 1. Récupérer les données
    const dataResponse = await this.getProductSalesData(startDate, endDate);

    if (!dataResponse.success) {
      return dataResponse;
    }

    // 2. Vérifier qu'il y a des données
    if (!dataResponse.data.products || dataResponse.data.products.length === 0) {
      return {
        success: false,
        message: 'Aucune vente sur cette période, rien à exporter'
      };
    }

    // 3. Générer et télécharger le fichier Excel
    return this.generateProductSalesExcel(dataResponse.data);
  },

  // =====================================================
  // =========== HELPERS PRIVÉS ==========================
  // =====================================================

  _buildFileName(exportData) {
    const boutiqueSlug = (exportData.boutiqueName || 'boutique')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

    const start = exportData.startDate || new Date().toISOString().slice(0, 10);
    const end = exportData.endDate || start;

    if (start === end) {
      return `ventes_produits_${boutiqueSlug}_${start}.xlsx`;
    }

    return `ventes_produits_${boutiqueSlug}_du_${start}_au_${end}.xlsx`;
  }
};

export default exportService;